import {
  boldFont,
  getRelativeFontSize,
  mediumFont,
  regularFont,
  theme,
} from "../../utils/styles";

const aboutVedazStyles = {
  // ─── Page Shell ────────────────────────────────────────────────
  pageContainer: {
    minHeight: "calc(100vh - 80px)",
    [theme.breakpoints.down("md")]: {
      marginTop: "10px",
    },
  },

  // ─── Navigation Wrapper ─────────────────────────────────────────
  navWrapper: {
    position: "sticky" as const,
    top: 0,
    zIndex: 100,
    // Subtle purple-tinted dark glass — not plain black
    background:
      "linear-gradient(180deg, rgba(20,12,40,0.98) 0%, rgba(8,8,22,0.97) 100%)",
    backdropFilter: "blur(28px)",
    WebkitBackdropFilter: "blur(28px)",
    borderBottom: "1px solid rgba(144,83,246,0.22)",
    boxShadow:
      "0 4px 32px rgba(0,0,0,0.55), 0 1px 0 rgba(144,83,246,0.06) inset",
  },

  // ── Main row: back | groups | counter + arrows ───────────────────
  navMainRow: {
    display: "flex",
    alignItems: "center",
    gap: { xs: "6px", sm: "8px" },
    px: { xs: 2, sm: 3, md: 4 },
    height: "54px",
    overflowX: "auto" as const,
    scrollbarWidth: "none" as const,
    "&::-webkit-scrollbar": { display: "none" },
  },

  // Back button — glassy pill
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.16s ease",
    borderRadius: "10px",
    px: "10px",
    py: "6px",
    color: "rgba(255,255,255)",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    "&:hover": {
      color: "#ffffff",
      background: "rgba(255,255,255,0.1)",
      borderColor: "rgba(255,255,255,0.2)",
      transform: "translateX(-1px)",
    },
  },
  backBtnText: {
    ...regularFont,
    fontSize: getRelativeFontSize(-2),
    lineHeight: 1,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  // Thin vertical separator
  navSep: {
    width: "1px",
    height: "22px",
    background: "rgba(255,255,255,0.1)",
    flexShrink: 0,
  },

  // ── Group chips ──────────────────────────────────────────────────

  // Inactive: ghost glass pill
  groupChip: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    px: { xs: "10px", sm: "14px" },
    py: "7px",
    borderRadius: "20px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.18s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.09)",
      borderColor: "rgba(255,255,255,0.2)",
      transform: "translateY(-1px)",
    },
  },
  // Active: vivid gradient pill with glow
  groupChipActive: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    px: { xs: "10px", sm: "14px" },
    py: "7px",
    borderRadius: "20px",
    background: "linear-gradient(135deg, #9053F6 0%, #5E75FF 100%)",
    border: "1px solid rgba(255,255,255,0.12)",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.18s ease",
    boxShadow:
      "0 4px 20px rgba(144,83,246,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset",
    "&:hover": {
      boxShadow:
        "0 6px 28px rgba(144,83,246,0.6), 0 0 0 1px rgba(255,255,255,0.1) inset",
      transform: "translateY(-1px)",
    },
  },

  // Number "01" inside chip
  groupChipNum: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-3),
    color: "rgba(144,83,246,0.75)",
    lineHeight: 1,
    letterSpacing: "0.5px",
  },
  groupChipNumActive: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-3),
    color: "rgba(255,255,255,0.65)",
    lineHeight: 1,
    letterSpacing: "0.5px",
  },

  // Group name label
  groupChipName: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-1),
    color: "rgba(255,255,255,0.7)",
    lineHeight: 1,
    whiteSpace: "nowrap" as const,
  },
  groupChipNameActive: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-1),
    color: "#ffffff",
    lineHeight: 1,
    whiteSpace: "nowrap" as const,
  },

  // Chapter-count badge (shown inside active multi-chapter chip)
  groupChipBadge: {
    background: "rgba(255,255,255,0.22)",
    borderRadius: "8px",
    px: "6px",
    py: "2px",
    ...mediumFont,
    fontSize: getRelativeFontSize(-4),
    color: "rgba(255,255,255,0.9)",
    lineHeight: 1,
    flexShrink: 0,
  },
  // Small dot count for inactive multi-chapter chip
  groupChipCount: {
    ...regularFont,
    fontSize: getRelativeFontSize(-3),
    color: "rgba(144,83,246,0.6)",
    lineHeight: 1,
  },

  // Spacer
  navSpacer: { flex: 1, minWidth: "4px" },

  // "06 / 10" counter badge
  chapterCounter: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-2),
    color: "rgba(255,255,255,0.7)",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "8px",
    px: "8px",
    py: "4px",
    lineHeight: 1,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  // Prev / Next arrows
  navArrow: {
    flexShrink: 0,
    width: "30px",
    height: "30px",
    borderRadius: "9px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "rgba(255,255,255,0.5)",
    transition: "all 0.16s ease",
    "&:hover": {
      background: "rgba(144,83,246,0.22)",
      borderColor: "rgba(144,83,246,0.5)",
      color: "#ffffff",
      transform: "scale(1.08)",
    },
  },
  navArrowDisabled: {
    flexShrink: 0,
    width: "30px",
    height: "30px",
    borderRadius: "9px",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",
    color: "rgba(255,255,255,0.1)",
    pointerEvents: "none" as const,
  },

  // ── Chapter sub-row (multi-chapter groups only) ──────────────────
  chapterSubRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    py: 2,
    px: { xs: 2, sm: 3, md: 4 },
    height: "36px",
    overflowX: "auto" as const,
    scrollbarWidth: "none" as const,
    "&::-webkit-scrollbar": { display: "none" },
    background: "rgba(144,83,246,0.04)",
    borderTop: "1px solid rgba(144,83,246,0.1)",
  },
  chapterPill: {
    flexShrink: 0,
    px: "10px",
    py: "10px",
    borderRadius: "12px",
    cursor: "pointer",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    ...regularFont,
    fontSize: getRelativeFontSize(1),
    color: "rgba(255,255,255,0.7)",
    whiteSpace: "nowrap" as const,
    lineHeight: 1,
    transition: "all 0.14s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.09)",
      borderColor: "rgba(255,255,255,0.2)",
      color: "rgba(255,255,255,0.75)",
    },
  },
  chapterPillActive: {
    flexShrink: 0,
    px: "10px",
    py: "10px",
    borderRadius: "12px",
    cursor: "pointer",
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "#ffffff",
    background: "linear-gradient(135deg, #9053F6 0%, #5E75FF 100%)",
    border: "1px solid rgba(255,255,255,0.12)",
    whiteSpace: "nowrap" as const,
    lineHeight: 1,
    boxShadow:
      "0 4px 20px rgba(144,83,246,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset",
  },

  // Reading progress bar
  navProgressBar: {
    height: "2px",
  },

  // ─── Content Area (full width) ─────────────────────────────────
  contentArea: {},
  contentInner: {
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    pb: "1px",
  },

  // ─── Breadcrumb ────────────────────────────────────────────────
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    px: 6,
    py: 3,
  },
  groupBadge: {
    background: "rgba(144,83,246,0.14)",
    border: "1px solid rgba(144,83,246,0.35)",
    borderRadius: "6px",
    px: "9px",
    py: "3px",
    ...mediumFont,
    fontSize: getRelativeFontSize(-3),
    color: "#9053F6",
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
    flexShrink: 0,
  },
  breadcrumbSep: {
    color: "rgba(255,255,255,0.18)",
    fontSize: "16px",
    lineHeight: 1,
  },
  breadcrumbChapter: {
    ...regularFont,
    fontSize: getRelativeFontSize(-1),
    color: "rgba(255,255,255,0.38)",
  },

  // ─── Next Chapter Footer Card ───────────────────────────────────
  nextChapterCard: {
    m: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    borderRadius: "18px",
    p: { xs: 2.5, sm: 4 },
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.07)",
    transition: "all 0.22s ease",
    "&:hover": {
      background: "rgba(144,83,246,0.08)",
      borderColor: "rgba(144,83,246,0.3)",
      transform: "translateX(6px)",
      boxShadow: "0 8px 40px rgba(144,83,246,0.1)",
    },
  },
  nextLabel: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-2),
    color: "rgba(255,255,255,0.28)",
    letterSpacing: "2px",
    textTransform: "uppercase" as const,
    mb: "6px",
  },
  nextTitle: {
    ...mediumFont,
    fontSize: getRelativeFontSize(6),
    color: "#ffffff",
    lineHeight: 1.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: getRelativeFontSize(3),
    },
  },
  nextArrow: {
    color: "#9053F6",
    fontSize: "32px",
    flexShrink: 0,
    ml: 2,
  },

  // ─── Section Hero ──────────────────────────────────────────────
  heroSection: {
    position: "relative" as const,
    overflow: "hidden" as const,
    pb: { xs: 3, sm: 4 },
    mb: { xs: 3, sm: 4 },
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
  },
  heroOrb: {
    position: "absolute" as const,
    top: "-80px",
    right: "-120px",
    width: "380px",
    height: "380px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(144,83,246,0.16) 0%, transparent 65%)",
    filter: "blur(64px)",
    pointerEvents: "none" as const,
    zIndex: 0,
  },
  heroOrbSecondary: {
    position: "absolute" as const,
    bottom: "-40px",
    left: "-60px",
    width: "240px",
    height: "240px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 65%)",
    filter: "blur(50px)",
    pointerEvents: "none" as const,
    zIndex: 0,
  },
  heroChapter: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-1),
    color: "#9053F6",
    letterSpacing: "3.5px",
    textTransform: "uppercase" as const,
    mb: 1.5,
    position: "relative" as const,
    zIndex: 1,
  },
  heroTitle: {
    ...mediumFont,
    color: "#ffffff",
    fontSize: getRelativeFontSize(18),
    lineHeight: 1.12,
    mb: 1.5,
    position: "relative" as const,
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      fontSize: getRelativeFontSize(12),
    },
  },
  heroSubtitle: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.48)",
    fontSize: getRelativeFontSize(3),
    maxWidth: "560px",
    lineHeight: 1.65,
    position: "relative" as const,
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      fontSize: getRelativeFontSize(1),
    },
  },

  // ─── Body Text ─────────────────────────────────────────────────
  bodyText: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.75)",
    fontSize: getRelativeFontSize(2),
    lineHeight: 1.85,
    mb: 2,
    maxWidth: "740px",
  },

  // ─── Section Headings ──────────────────────────────────────────
  sectionHeading: {
    ...boldFont,
    color: "#ffffff",
    fontSize: getRelativeFontSize(10),
    lineHeight: 1.2,
    mb: 2,
    [theme.breakpoints.down("sm")]: {
      fontSize: getRelativeFontSize(6),
    },
  },
  sectionSubheading: {
    ...mediumFont,
    color: "#9053F6",
    fontSize: getRelativeFontSize(5),
    letterSpacing: "1px",
    mb: 2,
    mt: -1,
  },

  // ─── TYPE 1: Background Image/Video Section ────────────────────
  // Full-bleed image; content inside a floating glass card
  bgSection: {
    position: "relative" as const,
    overflow: "hidden" as const,
    mb: 0,
    minHeight: { xs: "360px", sm: "460px", md: "560px" },
    display: "flex",
    alignItems: "center",
    background: "#080616",
    // Fine star-field: tiny 1px dots scattered on a large grid
    "&::after": {
      content: '""',
      position: "absolute" as const,
      inset: 0,
      backgroundImage: [
        "radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)",
        "radial-gradient(circle, rgba(144,83,246,0.25) 1px, transparent 1px)",
      ].join(", "),
      backgroundSize: "60px 60px, 97px 97px",
      backgroundPosition: "0 0, 30px 30px",
      zIndex: 2,
      pointerEvents: "none" as const,
    },
  },
  bgSectionMedia: {
    position: "absolute" as const,
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    zIndex: 0,
    transform: "scale(1.04)",
    transformOrigin: "center" as const,
  },
  // Left-heavy dark veil — image fully visible on the right half
  bgSectionOverlay: {
    position: "absolute" as const,
    inset: 0,
    background:
      "linear-gradient(108deg, rgba(6,4,20,0.97) 0%, rgba(6,4,20,0.82) 35%, rgba(6,4,20,0.35) 62%, rgba(6,4,20,0.04) 100%)",
    zIndex: 1,
  },
  // Chamfered glass card — left purple glow edge, no top hairline
  bgSectionContent: {
    position: "relative" as const,
    zIndex: 3,
    m: { xs: "20px", sm: "40px", md: "56px" },
    p: { xs: "24px", sm: "32px", md: "42px" },
    maxWidth: { xs: "100%", sm: "88%", md: "460px" },
    background: "rgba(8,6,26,0.55)",
    backdropFilter: "blur(32px)",
    WebkitBackdropFilter: "blur(32px)",
    borderTop: "1px solid rgba(255,255,255,0.05)",
    borderRight: "1px solid rgba(255,255,255,0.04)",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    borderLeft: "2px solid rgba(144,83,246,0.55)",
    boxShadow:
      "-6px 0 32px rgba(144,83,246,0.2), 0 40px 100px rgba(0,0,0,0.65)",
    clipPath:
      "polygon(0 0, 100% 0, 100% calc(100% - 22px), calc(100% - 22px) 100%, 0 100%)",
    "&::after": {
      content: '""',
      display: "block",
      width: "36px",
      height: "3px",
      background: "linear-gradient(90deg, #9053F6, #5E75FF)",
      borderRadius: "2px",
      marginBottom: "16px",
    },
  },
  // Unused — kept for type safety
  bgSectionChapterMark: {
    display: "none" as const,
  },

  // ─── TYPE 2: Side-by-side Image Section ────────────────────────
  // Nebula atmosphere: multi-point radial glows, no hard edges
  sideRow: {
    display: "flex",
    gap: { xs: 4, sm: 6 },
    mb: 0,
    p: { xs: "32px", sm: "56px" },
    alignItems: "center",
    position: "relative" as const,
    overflow: "hidden" as const,
    flexDirection: "row" as const,
    // Three floating glow orbs — no lines, no borders
    background: [
      "radial-gradient(circle at 6% 48%, rgba(144,83,246,0.22) 0%, transparent 40%)",
      "radial-gradient(circle at 94% 18%, rgba(94,117,255,0.15) 0%, transparent 34%)",
      "radial-gradient(circle at 62% 92%, rgba(144,83,246,0.1) 0%, transparent 30%)",
    ].join(", "),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column" as const,
    },
  },
  sideRowReverse: {
    display: "flex",
    gap: { xs: 4, sm: 6 },
    mb: 0,
    p: { xs: "32px", sm: "56px" },
    alignItems: "center",
    position: "relative" as const,
    overflow: "hidden" as const,
    flexDirection: "row-reverse" as const,
    // Mirrored orb positions
    background: [
      "radial-gradient(circle at 94% 48%, rgba(144,83,246,0.22) 0%, transparent 40%)",
      "radial-gradient(circle at 6% 18%, rgba(94,117,255,0.15) 0%, transparent 34%)",
      "radial-gradient(circle at 38% 92%, rgba(144,83,246,0.1) 0%, transparent 30%)",
    ].join(", "),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column" as const,
    },
  },
  // Image: L-bracket corners at top-left + bottom-right, halo glow, hover zoom
  sideImage: {
    position: "relative" as const,
    width: { xs: "100%", md: "370px" },
    flexShrink: 0,
    // Top-left L-bracket corner
    "&::before": {
      content: '""',
      position: "absolute" as const,
      top: "-10px",
      left: "-10px",
      width: "34px",
      height: "34px",
      borderTop: "2px solid #9053F6",
      borderLeft: "2px solid #9053F6",
      zIndex: 2,
      pointerEvents: "none" as const,
    },
    // Bottom-right L-bracket corner
    "&::after": {
      content: '""',
      position: "absolute" as const,
      bottom: "-10px",
      right: "-10px",
      width: "34px",
      height: "34px",
      borderBottom: "2px solid #9053F6",
      borderRight: "2px solid #9053F6",
      zIndex: 2,
      pointerEvents: "none" as const,
    },
    "& img": {
      width: "100%",
      display: "block" as const,
      position: "relative" as const,
      zIndex: 1,
      boxShadow:
        "0 32px 90px rgba(0,0,0,0.65), 0 0 0 1px rgba(144,83,246,0.12), 0 0 60px rgba(144,83,246,0.18)",
      transition: "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    },
    "&:hover img": { transform: "scale(1.05) translateY(-4px)" },
  },
  sideContent: {
    flex: 1,
    minWidth: 0,
    position: "relative" as const,
    zIndex: 1,
  },
  // Unused — kept for type safety
  sideChapterWatermark: {
    display: "none" as const,
  },

  // ─── TYPE 3: Plain Section — deep aurora atmosphere ────────────
  plainSection: {
    mb: 0,
    position: "relative" as const,
    overflow: "hidden" as const,
    p: { xs: "32px", sm: "56px" },
    // Aurora: large blurred orbs that look like northern lights
    background: [
      "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(144,83,246,0.28) 0%, transparent 65%)",
      "radial-gradient(ellipse 50% 40% at 0% 100%, rgba(94,117,255,0.16) 0%, transparent 58%)",
      "radial-gradient(ellipse 44% 36% at 100% 90%, rgba(144,83,246,0.12) 0%, transparent 55%)",
    ].join(", "),
    // Top aurora glow-burst (blurred orb, not a line)
    "&::before": {
      content: '""',
      position: "absolute" as const,
      top: "-80px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "60%",
      height: "200px",
      background:
        "radial-gradient(ellipse at center, rgba(144,83,246,0.4) 0%, transparent 70%)",
      filter: "blur(50px)",
      pointerEvents: "none" as const,
      zIndex: 0,
    },
    // Faint cosmic ring bottom-right
    "&::after": {
      content: '""',
      position: "absolute" as const,
      bottom: "-60px",
      right: "-60px",
      width: "280px",
      height: "280px",
      borderRadius: "50%",
      border: "1px solid rgba(144,83,246,0.12)",
      boxShadow:
        "0 0 0 30px rgba(144,83,246,0.04), 0 0 0 60px rgba(144,83,246,0.02)",
      pointerEvents: "none" as const,
      zIndex: 0,
    },
  },

  // ─── Numbered Accordion ────────────────────────────────────────
  accordionWrap: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
  },
  accordionItem: {
    position: "relative" as const,
    overflow: "hidden" as const,
    borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.07)",
    background: "rgba(255,255,255,0.03)",
    transition: "border-color 0.2s ease, background 0.2s ease",
    "&:hover": {
      borderColor: "rgba(144,83,246,0.28)",
      background: "rgba(144,83,246,0.05)",
    },
  },
  accordionItemOpen: {
    position: "relative" as const,
    overflow: "hidden" as const,
    borderRadius: "14px",
    border: "1px solid rgba(144,83,246,0.45)",
    background: "rgba(144,83,246,0.08)",
    boxShadow: "0 8px 40px rgba(144,83,246,0.14)",
  },
  accordionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    px: { xs: "18px", sm: "24px" },
    py: "18px",
    cursor: "pointer",
    position: "relative" as const,
    zIndex: 1,
    userSelect: "none" as const,
  },
  accordionGhostNum: {
    position: "absolute" as const,
    right: "56px",
    top: "50%",
    transform: "translateY(-50%)",
    ...mediumFont,
    fontSize: "64px",
    lineHeight: 1,
    letterSpacing: "-4px",
    color: "rgba(255,255,255,0.04)",
    pointerEvents: "none" as const,
    userSelect: "none" as const,
    zIndex: 0,
  },
  accordionGhostNumOpen: {
    position: "absolute" as const,
    right: "56px",
    top: "50%",
    transform: "translateY(-50%)",
    ...mediumFont,
    fontSize: "64px",
    lineHeight: 1,
    letterSpacing: "-4px",
    color: "rgba(144,83,246,0.18)",
    pointerEvents: "none" as const,
    userSelect: "none" as const,
    zIndex: 0,
  },
  accordionIndexBadge: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...mediumFont,
    fontSize: getRelativeFontSize(-3),
    color: "rgba(255,255,255,0.4)",
    lineHeight: 1,
  },
  accordionIndexBadgeOpen: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    background: "rgba(144,83,246,0.25)",
    border: "1px solid rgba(144,83,246,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...mediumFont,
    fontSize: getRelativeFontSize(-3),
    color: "#ffffff",
    lineHeight: 1,
    boxShadow: "0 0 10px rgba(144,83,246,0.3)",
  },
  accordionTitle: {
    ...mediumFont,
    fontSize: getRelativeFontSize(3),
    color: "rgba(255,255,255,0.55)",
    flex: 1,
    lineHeight: 1.2,
    zIndex: 1,
  },
  accordionTitleOpen: {
    ...mediumFont,
    fontSize: getRelativeFontSize(3),
    color: "#ffffff",
    flex: 1,
    lineHeight: 1.2,
    zIndex: 1,
  },
  accordionChevron: {
    fontSize: "16px",
    color: "rgba(255,255,255,0.25)",
    flexShrink: 0,
    transition: "transform 0.22s ease, color 0.22s ease",
    zIndex: 1,
  },
  accordionChevronOpen: {
    fontSize: "16px",
    color: "#9053F6",
    flexShrink: 0,
    transform: "rotate(180deg)",
    transition: "transform 0.22s ease, color 0.22s ease",
    zIndex: 1,
  },
  accordionBody: {
    px: { xs: "18px", sm: "24px" },
  },

  // ─── Quote Block ───────────────────────────────────────────────
  quoteBlock: {
    position: "relative" as const,
    overflow: "hidden" as const,
    borderLeft: "3px solid",
    borderImageSlice: 1,
    borderImageSource: "linear-gradient(to bottom, #9053F6, #5E75FF)",
    pl: 4,
    pr: 3,
    py: "20px",
    mt: 3,
    background:
      "linear-gradient(135deg, rgba(144,83,246,0.09) 0%, rgba(94,117,255,0.05) 100%)",
    borderRadius: "0 14px 14px 0",
    // Giant decorative opening curly-quote
    "&::before": {
      content: '"\u201C"',
      position: "absolute" as const,
      top: "-14px",
      left: "10px",
      fontSize: "88px",
      lineHeight: 1,
      color: "rgba(144,83,246,0.28)",
      fontFamily: "Georgia, 'Times New Roman', serif",
      pointerEvents: "none" as const,
      userSelect: "none" as const,
    },
  },
  quoteText: {
    ...regularFont,
    color: "rgba(255,255,255,0.82)",
    fontSize: getRelativeFontSize(2),
    lineHeight: 1.9,
    fontStyle: "italic",
    position: "relative" as const,
    zIndex: 1,
    pt: "14px",
  },

  // ─── Feature Cards ─────────────────────────────────────────────
  featureCard: {
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "16px",
    p: { xs: 2.5, sm: 3 },
    height: "100%",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(144, 83, 246, 0.07)",
      borderColor: "rgba(144, 83, 246, 0.35)",
      transform: "translateY(-3px)",
      boxShadow: "0 8px 32px rgba(144,83,246,0.12)",
    },
  },
  featureNumber: {
    ...mediumFont,
    fontSize: getRelativeFontSize(16),
    color: "rgba(144, 83, 246, 0.22)",
    lineHeight: 1,
    mb: 2,
    userSelect: "none" as const,
    letterSpacing: "-1px",
  },
  featureTitle: {
    ...mediumFont,
    color: "#ffffff",
    fontSize: getRelativeFontSize(4),
    mb: 1,
  },
  featureBody: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: getRelativeFontSize(1),
    lineHeight: 1.78,
  },

  // ─── Stats Row ─────────────────────────────────────────────────
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: { xs: 1.5, sm: 2 },
    mt: 4,
  },
  statItem: {
    textAlign: "center" as const,
    background: "rgba(144, 83, 246, 0.1)",
    border: "1px solid rgba(144, 83, 246, 0.2)",
    borderRadius: "16px",
    p: { xs: 2, sm: 3 },
    transition: "border-color 0.2s ease",
    "&:hover": { borderColor: "rgba(144, 83, 246, 0.42)" },
  },
  statValue: {
    ...mediumFont,
    color: "#9053F6",
    fontSize: getRelativeFontSize(14),
    lineHeight: 1.1,
    [theme.breakpoints.down("sm")]: { fontSize: getRelativeFontSize(8) },
  },
  statLabel: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.46)",
    fontSize: getRelativeFontSize(0),
    mt: 0.75,
    [theme.breakpoints.down("sm")]: { fontSize: getRelativeFontSize(-1) },
  },

  // ─── Bullet List ───────────────────────────────────────────────
  bulletRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
    mb: 2,
  },
  bulletBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    background: "rgba(144, 83, 246, 0.16)",
    border: "1px solid rgba(144, 83, 246, 0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mt: "3px",
  },
  bulletDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#9053F6",
  },
  bulletText: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.78)",
    fontSize: getRelativeFontSize(2),
    lineHeight: 1.7,
  },

  // ─── Compat Level Card ─────────────────────────────────────────
  compatCard: {
    borderRadius: "14px",
    p: { xs: 2.5, sm: 3 },
    mb: "12px",
    border: "1px solid rgba(255,255,255,0.08)",
    borderLeft: "4px solid transparent",
    transition: "all 0.18s ease",
    "&:hover": { transform: "translateX(4px)" },
  },
  compatLabel: {
    ...mediumFont,
    fontSize: getRelativeFontSize(3),
    mb: 0.75,
  },
  compatBody: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.68)",
    fontSize: getRelativeFontSize(1),
    lineHeight: 1.78,
  },

  // ─── Analysis Dimension Cards ──────────────────────────────────
  dimensionCard: {
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "14px",
    p: { xs: 2, sm: 2.5 },
    cursor: "pointer" as const,
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
    "&:hover": {
      background: "rgba(144, 83, 246, 0.12)",
      borderColor: "rgba(144, 83, 246, 0.42)",
      transform: "translateY(-3px)",
      boxShadow: "0 8px 24px rgba(144,83,246,0.14)",
    },
  },
  dimensionLabel: {
    ...mediumFont,
    color: "#ffffff",
    fontSize: getRelativeFontSize(3),
  },
  dimensionArrow: {
    ...regularFont,
    color: "rgba(144, 83, 246, 0.72)",
    fontSize: getRelativeFontSize(0),
  },

  // ─── Highlight Block ───────────────────────────────────────────
  highlightBox: {
    background: "rgba(144, 83, 246, 0.08)",
    border: "1px solid rgba(144, 83, 246, 0.2)",
    borderLeft: "3px solid #9053F6",
    borderRadius: "0 12px 12px 0",
    p: 2.5,
    mt: 3,
    mb: 2,
  },
  highlightText: {
    ...mediumFont,
    color: "rgba(255, 255, 255, 0.85)",
    fontSize: getRelativeFontSize(2),
    lineHeight: 1.7,
  },

  // ─── Risk Group Callout ────────────────────────────────────────
  riskGroupCard: {
    background: "rgba(255, 193, 7, 0.07)",
    border: "1px solid rgba(255, 193, 7, 0.2)",
    borderRadius: "14px",
    p: { xs: 2.5, sm: 3 },
    mt: 3,
  },
  riskGroupTitle: {
    ...mediumFont,
    color: "#FFC107",
    fontSize: getRelativeFontSize(3),
    mb: 1,
  },
  riskGroupBody: {
    ...regularFont,
    color: "rgba(255, 255, 255, 0.68)",
    fontSize: getRelativeFontSize(1),
    lineHeight: 1.75,
  },
} as const;

export default aboutVedazStyles;
